import { isEmpty } from 'lodash';
import {
    RETIREMENT_CATEGORY_GOALS,
    WEALTH_CATEGORY_GOALS,
    FLOW2_CATEGORY_GOALS,
    WeightOptions,
    WeightTypeOptions
} from './constants';
import {
    AdviceClientType,
    FundingSource,
    GoalPriorityEnum,
    GoalTitleEnum,
    GoalTypeProps,
    ClientStagesType,
    Flow2GoalTypeProps
} from './types';
import dayjs from 'dayjs';
import { NavigateFunction } from 'react-router-dom';

let navigate: NavigateFunction | null = null;
// To enable navigation from plain functions
export function setNavigate(navigateFn: NavigateFunction) {
    navigate = navigateFn;
}

export function navigateTo(path: string, replace: boolean = false) {
    if (navigate) {
        navigate(path, { replace });
    } else {
        console.error('Navigate function has not been set.');
    }
}

export const isEmailValid = (value: string) =>
    Boolean(value === '' || value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i));

export const isAlphaNumValid = (value: string) => value === '' || value.match(/^[a-zA-Z0-9]+$/);

export const isAlphaNumSpacesValid = (value: string) => value === '' || value.match(/^[a-zA-Z0-9\s]+$/);

export const isAlphaOnlyValid = (value: string) => value === '' || value.match(/^[a-zA-Z]+$/);

export const isAlphaSpacesOnlyValid = (value: string) => value === '' || value.match(/^[a-zA-Z\s]+$/);

// Regex for generic phone number added. TODO get confg from organization
// Allowed patterns
// (123) 456-7890
// 123-456-7890
// 123.456.7890
// 1234567890
// +31636363634
// 075-63546725
// 022-635463933
export const isPhoneNumberValid = (value: string) =>
    // eslint-disable-next-line no-useless-escape
    value === '' || value.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im);

export const isNumberInRange = (val = 0, min = 0, max = 0) => val >= min && val <= max;

export const hex2rgba = (hex: any, alpha = 1) => {
    const [r, g, b] = hex.match(/\w\w/g).map((x: string) => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getFullMonthFromClientDOB = (dob: string) => {
    return new Date(dob?.split('-')[1]).toLocaleString('default', { month: 'long' });
};

export const getFullYearFromClientDOB = (dob: string) => {
    return dob?.split('-')[2];
};

export const isStageDisabled = (adviceClient: AdviceClientType, stage: string) => {
    // enable all stages after first stage is completed i.e., stages array has > 1 items
    return !adviceClient?.stages?.includes(stage) && !(adviceClient?.stages?.length > 1);
};

export const formatCurrency = (num: any, doNotAllowZero = true) => {
    if (num) {
        let value = num?.toString();
        value = value.replace(/[^.0-9]/g, '');

        if (doNotAllowZero) {
            value = value.replace(/\b0+/g, '');
        }

        let splittedValue = value.split('.');

        if (splittedValue[1]?.length > 2) {
            splittedValue[1] = splittedValue[1][0] + splittedValue[1][1];
            value = [splittedValue[0], splittedValue[1]].join('.');
        }

        if (splittedValue?.length > 2) {
            value = [splittedValue[0], splittedValue[1]].join('.');
        }

        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return value;
    }
    return '';
};

export const getMMYYYYfromTimeStamp = (date: string) => {
    return formatDate(date, 'MM/YYYY');
};

export const getYYYYMMDDfromDate = (value: any, format: string = 'DD-MM-YYYY') => {
    // eslint-disable-next-line no-useless-escape
    const regex = /^(\d{2}\-\d{2}\-\d{4})$/;
    if (typeof value === 'string' && regex.test(value)) return value;
    return value['format'] ? value?.format(format) : value;
};

export const getFormattedDate = (value: any, format: string = 'MMMM, YYYY') => {
    return value.format(format);
};

export const getHeaderIconFromType = (goalTitle?: GoalTitleEnum) => {
    if (goalTitle)
        return [...RETIREMENT_CATEGORY_GOALS, ...WEALTH_CATEGORY_GOALS, ...FLOW2_CATEGORY_GOALS].find(
            (g: GoalTypeProps | Flow2GoalTypeProps) => g.title === goalTitle
        )?.type;
    return WEALTH_CATEGORY_GOALS[0].type;
};

export const groupGoalsByPriority = (values: any, sortCallback: any) => {
    let sorted = values.reduce((a: any, b: any) => {
        const key = typeof sortCallback === 'function' ? sortCallback(b) : b[sortCallback];
        if (!a[key]) {
            a[key] = [b];
        } else {
            a[key] = [...a[key], b];
        }

        return a;
    }, {});
    return Object.values(GoalPriorityEnum)
        .map((gp) => sorted[gp])
        .filter((gp) => gp)
        .flat();
};

export const groupGoalsByLastModified = (values: any[]) => {
    return values.sort((a, b) => {
        return (
            new Date(b?.data?.updatedAt || b?.data?.createdAt).getTime() -
            new Date(a?.data?.updatedAt || a?.data?.createdAt).getTime()
        );
    });
};

export function formatAdvisorName(data: any) {
    return !isEmpty(data) && data?.userType === 'admin' ? data?.adminName || '' : data?.advisorName || '';
}

export const formatDate = (date?: string | Date, format = 'MM/DD/YYYY') => {
    if (!date || !dayjs(date).isValid()) {
        return '-';
    }

    return dayjs(date).format(format);
};

export const convertDateFormat = (dateStr: string) => {
    // Regular expression to check if the date is in dd-mm-yyyy format
    const regex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/;

    // Check if the dateStr matches the dd-mm-yyyy format
    if (!regex.test(dateStr)) {
        return dateStr;
    }

    // Split the input string by '-'
    const parts = dateStr.split('-');
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];

    // Create the mm-dd-yyyy format
    const convertedDate = `${month}-${day}-${year}`;
    return convertedDate;
};

export const convertDateFormatReverse = (dateStr: string) => {
    // Regular expression to check if the date is in mm-dd-yyyy format
    const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/;

    // Check if the dateStr matches the mm-dd-yyyy format
    if (!regex.test(dateStr)) {
        return dateStr;
    }

    // Split the input string by '-'
    const parts = dateStr.split('-');
    const month = parts[0];
    const day = parts[1];
    const year = parts[2];

    // Create the dd-mm-yyyy format
    const convertedDate = `${day}-${month}-${year}`;
    return convertedDate;
};

export const isClientInfoCompleted = (adviceClient: AdviceClientType) => {
    return !!(
        adviceClient?.clientFirstName &&
        adviceClient?.clientLastName &&
        adviceClient?.clientDob &&
        adviceClient?.retirementAge &&
        adviceClient?.planningAge
    );
};

export const shouldPromptForIncome = (adviceClient: AdviceClientType) => {
    const hasIncome = !!(adviceClient?.accounts?.incomeSources && adviceClient.accounts.incomeSources.length > 0);
    return !hasIncome;
};

export const shouldPromptForAccounts = (adviceClient: AdviceClientType) => {
    const hasAccounts = !!(
        adviceClient?.accounts?.investmentsSources && adviceClient.accounts.investmentsSources.length > 0
    );
    return !hasAccounts;
};

export const isStep1Completed = (stages: ClientStagesType[], adviceClient: AdviceClientType) =>
    stages
        .filter((stage) => !stage.optional)
        .every((stage) => {
            const hasStage = adviceClient?.stages?.includes(stage.key);

            const hasAtLeastOneFundingSource = Object.keys(adviceClient.accounts).some((key) => {
                const fundingSource = adviceClient.accounts?.[key as AccountKeyName];
                return Array.isArray(fundingSource) && fundingSource.length > 0;
            });
            return hasStage && hasAtLeastOneFundingSource;
        });
type AccountKeyName = keyof FundingSource;

export const removeNullUndefined = (obj: any) => {
    if (obj === null || obj === undefined) {
        return {}; // Return an empty object if the input is null or undefined
    }

    // Create a new object without null or undefined values
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== null && value !== undefined));
};

export const stylizeXAxisLabel = (label?: string) => {
    const y = dayjs(label).year().toString();
    const dm = dayjs(label).format('MMM D');
    return `<span style="font-weight: 500;">${dm}</span><br/><span style="font-weight: 600;">${y}</span>`;
};

export const stylizeTooltipHeader = (label?: string) => {
    const y = dayjs(label).year().toString();
    const dm = dayjs(label).format('MMM D');
    return `<span style="font-size: 16px; font-weight: 700; line-height: 20px;">${dm}, ${y}</span>`;
};

type WealthPathResponse = number[] | { default?: number[]; pessimistic?: number[]; optimistic?: number[] };

export const buildWealthPathChartData = (
    wealthPath?: WealthPathResponse,
    xAxisLabels?: string[],
    startYear: number = new Date().getFullYear()
) => {
    const targets = (Array.isArray(wealthPath) ? wealthPath : wealthPath?.default) ?? [];
    const optimistic = (Array.isArray(wealthPath) ? undefined : wealthPath?.optimistic) ?? [];
    const pessimistic = (Array.isArray(wealthPath) ? undefined : wealthPath?.pessimistic) ?? [];
    const length = Math.max(targets.length, xAxisLabels?.length ?? 0);

    let bankruptcyYear: number | undefined = undefined;

    return Array.from({ length }, (_, index) => {
        const label = xAxisLabels?.[index];
        const year = label ? dayjs(label).year() : startYear + index;
        if (targets[index] === 0 && !bankruptcyYear) {
            bankruptcyYear = year;
        }
        return {
            year,
            target: targets[index],
            low: pessimistic[index] ?? 0,
            high: optimistic[index] ?? 0,
            bankruptcyHigh: bankruptcyYear ? Math.max(...optimistic, 0) : 0,
            bankruptcyLow: 0,
            ...(!!label && {
                xAxis: stylizeXAxisLabel(label),
                tooltipHeader: stylizeTooltipHeader(label)
            })
        };
    }).filter((item) => item.target != undefined);
};

export const splitClientName = (fullName: string) => {
    if (!fullName) return { firstName: '', lastName: '' };

    const spaceIndex = fullName.indexOf(' ');
    if (spaceIndex === -1) {
        return { firstName: fullName, lastName: '' };
    }

    return {
        firstName: fullName.substring(0, spaceIndex),
        lastName: fullName.substring(spaceIndex + 1)
    };
};

export function getWeightTypeLabel(value: string): string {
    const found = WeightTypeOptions.find((opt) => opt.value === value);
    return found ? found.label : value;
}

export function getFundTypeLabel(value: string): string {
    const found = WeightOptions.find((opt) => opt.value === value);
    return found ? found.label : value;
}

// Function to add age to a given DD-MM-YYYY date and return the new date in the outputFormat
export const addAgeToDate = (
    dob: string | undefined,
    age: string | number | undefined,
    outputFormat: string = 'DD-MM-YYYY'
): string | null => {
    if (!dob || !age) return null;
    return dayjs(dob, 'DD-MM-YYYY').add(+age, 'year').format(outputFormat);
};

export const toLowercaseKeys = (obj?: Record<string, any>) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return obj;
    }

    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key.toLowerCase()] = value;
        return acc;
    }, {} as Record<string, any>);
};

export const normalizeGoalPriorityKey = (priority?: string) => {
    if (typeof priority !== 'string') {
        return '';
    }

    return priority.trim().toLowerCase();
};

export const getGoalPriorityProbability = (
    goalPriorityProbabilities?: Record<string, number>,
    goalPriority?: string
) => {
    if (!goalPriorityProbabilities || typeof goalPriorityProbabilities !== 'object') {
        return 0;
    }

    const normalizedPriority = normalizeGoalPriorityKey(goalPriority);
    if (!normalizedPriority) {
        return 0;
    }

    return Number(
        goalPriorityProbabilities[normalizedPriority] ??
            goalPriorityProbabilities[goalPriority as keyof typeof goalPriorityProbabilities] ??
            0
    );
};

export const sortObject = (obj: Record<string, number>, keyOrder: string[]) => {
    return keyOrder.reduce((acc, key) => {
        if (key in obj) {
            acc[key] = obj[key];
        }
        return acc;
    }, {} as Record<string, number>);
};

export const getFirstNEntriesFromObj = (obj: Record<string, any> = {}, n?: number) => {
    return Object.fromEntries(Object.entries(obj).slice(0, n));
};
